/* eslint-disable @typescript-eslint/no-explicit-any */
import Modal from '@/shared/components/Modal/Modal'
import Button from '@/shared/components/Button/Button'
import DynamicForm, {
  StepFieldConfig,
  FormFieldConfig
} from '@/shared/components/DynamicForm/DynamicForm'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './DetailsModal.module.scss'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'

interface DetailsModalProps {
  formConfig: any
  title: string
  onClose: () => void
  isOpen: boolean
}

function DetailsModal(props: DetailsModalProps) {
  const { formConfig, title, onClose, isOpen } = props
  const isDirty = useFormDirtyStore(s => s.isDirty)
  const setIsDirty = useFormDirtyStore(s => s.setIsDirty)
  const resetIsDirty = useFormDirtyStore(s => s.reset)
  const { t } = useTranslation()
  const [steps, setSteps] = useState<StepFieldConfig[] | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [visibleStepsCount, setVisibleStepsCount] = useState(0)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  // Reset to first step when modal opens or form config changes
  useEffect(() => {
    if (isOpen) {
      setActiveStep(0)
    }
  }, [isOpen, formConfig.id])

  /*
    The auto-translate button only belongs on a step that actually holds
    translated fields. `syncFormField` is what marks one — it is the field that
    renders once per language — so the step is searched for it rather than being
    listed by name somewhere, which would go stale the moment a field moves.
  */
  const hasSyncField = (
    fields?: (FormFieldConfig | StepFieldConfig)[]
  ): boolean =>
    (fields ?? []).some(field => {
      // `in` rather than a check on `type`: FormFieldConfig types `type` as a
      // plain string, so it does not narrow the union on its own.
      if ('fields' in field) return hasSyncField(field.fields)
      // `input-group` and the like nest their children under `inputs`.
      return !!field.syncFormField || hasSyncField(field.inputs)
    })

  const activeStepHasTranslations = hasSyncField(steps?.[activeStep]?.fields)

  const isFirstStep = activeStep === 0
  const isLastStep = activeStep >= visibleStepsCount - 1

  const formErrorMessage = (
    <>
      {t('DETAILS_MODAL.FORM_ERROR_MESSAGE.FIRST_PART')}
      <br />
      {t('DETAILS_MODAL.FORM_ERROR_MESSAGE.SECOND_PART')}
      <mark className="mark-orange">
        <i className="las la-exclamation-circle icon-medium" />
      </mark>
      {t('DETAILS_MODAL.FORM_ERROR_MESSAGE.THIRD_PART')}
    </>
  )

  const handleNext = () => {
    if (activeStep < visibleStepsCount - 1) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1)
    }
  }

  const handleOnConfirmClose = () => {
    resetIsDirty()
    onClose()
    setIsConfirmationModalOpen(false)
  }

  const handleOnClose = () => {
    if (isDirty) {
      setIsConfirmationModalOpen(true)
    } else {
      handleOnConfirmClose()
    }
  }

  return (
    <>
      <Modal
        size="medium"
        title={title}
        onClose={handleOnClose}
        isOpen={isOpen}
      >
        <div className={styles.detailsModalContainer}>
          <DynamicForm
            key={formConfig.id}
            {...formConfig}
            superCondensed
            formErrorMessage={formErrorMessage}
            setSteps={setSteps}
            setActiveStep={setActiveStep}
            activeStep={activeStep}
            setVisibleStepsCount={setVisibleStepsCount}
            setIsDirty={setIsDirty}
            /*
              The row lives inside the form rather than beside it because
              `footerAction` is the auto-translate button, which reads and writes
              the form through `useFormContext`. Rendered as a sibling it had no
              provider above it and threw on mount. Same arrangement as
              AccordionItemModal, which already had to solve this.
            */
            FormActions={
              <div className={styles.detailsModalButtonContainer}>
                <Button
                  color="primary"
                  label={t('COMMON.CONFIRM')}
                  variant="contained"
                  type="submit"
                  form={formConfig.id}
                />
                <div className={styles.detailsModalRightActions}>
                  {formConfig.footerAction && activeStepHasTranslations
                    ? formConfig.footerAction
                    : null}
                  {steps?.length && visibleStepsCount > 1 ? (
                    <div className={styles.detailsModalNavigationButtonContainer}>
                      <Button
                        color="primary"
                        label={t('COMMON.BACK')}
                        variant="contained"
                        disabled={isFirstStep}
                        onClick={handleBack}
                      />

                      <Button
                        color="primary"
                        label={t('COMMON.NEXT')}
                        variant="contained"
                        disabled={isLastStep}
                        onClick={handleNext}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            }
          />
        </div>
      </Modal>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={handleOnConfirmClose}
        onClose={() => setIsConfirmationModalOpen(false)}
        title={t('CONFIRMATION_MODAL.TITLE')}
        message={t('CONFIRMATION_MODAL.DESCRIPTION')}
      />
    </>
  )
}

export default DetailsModal
